import { useState, useCallback, useEffect } from 'react';
import { getJuthoorParticipants } from '@/lib/api/juthoor';
import type { JuthoorParticipantEntry } from '@/types/juthoor';
import type { PaginationMeta } from '@/types/api';

export function useJuthoorParticipants(
  initiativeId: string,
  page: number = 1
): {
  data: JuthoorParticipantEntry[];
  meta: PaginationMeta | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  const [data, setData] = useState<JuthoorParticipantEntry[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchParticipants = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getJuthoorParticipants(initiativeId, { page, per_page: 20 });
      if (response.success && response.data) {
        // Backend wraps in { data, meta } inside the envelope's data field
        const payload = response.data as unknown as { data: JuthoorParticipantEntry[]; meta: PaginationMeta };
        if (Array.isArray(payload.data)) {
          setData(payload.data);
          setMeta(payload.meta ?? null);
        } else {
          setData(response.data as unknown as JuthoorParticipantEntry[]);
        }
      } else {
        setError(response.message || 'Failed to load participants');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load participants');
    } finally {
      setLoading(false);
    }
  }, [initiativeId, page]);

  useEffect(() => {
    fetchParticipants();
  }, [fetchParticipants]);

  return { data, meta, loading, error, refetch: fetchParticipants };
}
